import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type DeliveryTrack = {
  state: string;
  lastLat: string | null;
  lastLng: string | null;
  destinationLat: string | null;
  destinationLng: string | null;
  originLat: string | null;
  originLng: string | null;
  lastPingAt: string | null;
  assignedAt: string | null;
  acceptedAt: string | null;
  enRouteAt: string | null;
  arrivedAt: string | null;
  deliveredAt: string | null;
};

export function useDeliveryTracking(deliveryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.delivery.track(deliveryId),
    queryFn: () => requestJson<DeliveryTrack>(`/api/deliveries/${deliveryId}/track`),
    enabled: Boolean(deliveryId),
    refetchInterval: 15_000,
  });
}

export type DeliveryRowForOrder = {
  id: string;
  state: string;
  riderId: string | null;
  lastLat: string | null;
  lastLng: string | null;
  lastPingAt: string | null;
  // Delivery address snapshot: canonical address copy on the Delivery row.
  addressSnapshot: {
    label?: string | null;
    line1?: string;
    line2?: string | null;
    city?: string | null;
    country?: string;
    lat?: string | number | null;
    lng?: string | number | null;
    notes?: string | null;
  } | null;
  // What the Rider reported about the cash on completing a COD Delivery.
  // `not_collected` is the discrepancy staff have to chase.
  cashCollection?: "collected" | "not_collected" | null;
  cashCollectionAt?: string | null;
};

export function useDeliveryForOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.delivery.byOrder(orderId),
    queryFn: () =>
      requestJson<{ delivery: DeliveryRowForOrder | null }>(`/api/orders/${orderId}/delivery`),
    enabled: Boolean(orderId),
    select: (data) => data.delivery,
  });
}

export type OrderRating = {
  id: string;
  stars: number;
  comment: string | null;
  createdAt: string;
} | null;

export function useOrderRating(orderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.orderRating.detail(orderId),
    queryFn: () => requestJson<{ rating: OrderRating }>(`/api/orders/${orderId}/rating`),
    enabled: Boolean(orderId),
    select: (data) => data.rating,
  });
}

export function useSubmitRating(orderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { stars: number; comment?: string | null }) =>
      requestJson<{ rating: OrderRating }>(`/api/orders/${orderId}/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.orderRating.detail(orderId) });
    },
  });
}

export type SupportCategory =
  | "rider_issue"
  | "food_issue"
  | "missing_item"
  | "wrong_item"
  | "late"
  | "other";

export function useSubmitSupport(orderId: string | undefined) {
  return useMutation({
    mutationFn: (input: { category: SupportCategory; message: string }) =>
      requestJson<{ supportRequest: unknown }>(`/api/orders/${orderId}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  });
}
